import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ResetButton from '../../../components/common/ResetButton';
import InvoiceFilter from '../../../components/Billing/Invoice/InvoiceFilter';
import InvoiceTable from '../../../components/Billing/Invoice/InvoiceTable';
import InvoicePagination, { InvoiceStatusChipGroup } from '../../../components/Billing/Invoice/InvoicePagination';
import { useInvoicesQuery } from './invoiceHelper';
import { useClinic } from '../../../components/ClinicProvider';
import InvoiceHeader from './InvoiceHeader';
import { Chip } from '@mui/material';
import useCurrency from '../../../components/CurrencyProvider';
import { formatDateDMY } from '../../../utils/dateUtils';

// --- Types ---
interface InvoiceFilterValues {
  fromDate: string;
  toDate: string;
  billType: string;
}

// --- Constants ---
const DEFAULT_LIMIT = 10;
const DEFAULT_CATEGORY = 'Patient';

// --- Main Invoice Page ---
const Invoice = () => {
  const { t : tBilling } = useTranslation('billing_config');
  // --- State ---
  const [activeCategory, setActiveCategory] = useState(DEFAULT_CATEGORY);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_LIMIT);
  const [selectedStatus, setSelectedStatus] = useState<{ value: string; count: number } | null>(null);
  const [filterOpenLegacy, setFilterOpenLegacy] = useState(false); // Modal legacy
  const [filterValues, setFilterValues] = useState<InvoiceFilterValues>({ fromDate: '', toDate: '', billType: '' });
  
  // Filter handlers
  const handleOpenLegacyFilter = () => setFilterOpenLegacy(true);  // Legacy Modal
  const handleCloseLegacyFilter = () => setFilterOpenLegacy(false);

  const handleApplyFilter = (values: InvoiceFilterValues) => {
    if (
      values.fromDate !== filterValues.fromDate ||
      values.toDate !== filterValues.toDate ||
      values.billType !== filterValues.billType
    ) {
      setFilterValues(values);
      setCurrentPage(1); // Reset to first page on filter
    }
  };

  const navigate = useNavigate();
  const { ClinicId } = useClinic();
  const { clinicName } = useCurrency();
  // const { showLoader, hideLoader } = useLoader();
  
  // --- Handlers ---
  const handleCategoryChange = useCallback((cat: { label: string }) => setActiveCategory(cat.label), []);
  const handleSearchChange = useCallback((val: string) => setSearch(val), []);
  const handleStatusSelect = useCallback((status: { value: string; count: number }) => {
    if (status.value !== selectedStatus?.value) {
      setSelectedStatus(status);
      setCurrentPage(1); // Reset to first page on status change
    }
  }, [selectedStatus]);
  const handleNewInvoice = useCallback(() => { navigate('/admin/billing/invoice/new'); }, [navigate]);
  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // Reset handler
  const handleReset = useCallback(() => {
    setActiveCategory(DEFAULT_CATEGORY);
    setSearch('');
    setSelectedStatus(null);
    setFilterValues({ fromDate: '', toDate: '', billType: '' });
    setPageSize(DEFAULT_LIMIT);
    setCurrentPage(1);
  }, []);

  // Map UI category to API invoice_patient_type
  const categoryFilter = useMemo(
    () => activeCategory.toLowerCase() as ('patient' | 'donor' | 'surrogate' | 'company' | 'cryo'),
    [activeCategory]
  );

  // Debug: log query params and selectedStatus before query
  // eslint-disable-next-line no-console
  console.log('Query params:', {
    clinic: Number(ClinicId),
    invoice_patient_type: categoryFilter,
    search_query: search || undefined,
    from_date: filterValues.fromDate || undefined,
    to_date: filterValues.toDate || undefined,
    bill_type: (['Clinical', 'PH'].includes(filterValues.billType) ? filterValues.billType : undefined),
    status: selectedStatus?.value?.toLowerCase() || undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    enabled: !!ClinicId
  });

  // Query
  const { data, isFetching, error } = useInvoicesQuery({
    clinic: Number(ClinicId),
    invoice_patient_type: categoryFilter,
    search_query: search || undefined,
    from_date: filterValues.fromDate || undefined,
    to_date: filterValues.toDate || undefined,
    bill_type: (['Clinical', 'PH', 'Package'].includes(filterValues.billType) ? filterValues.billType : undefined) as 'Clinical' | 'Pharmacy' | 'PH' | 'Package' | undefined,
    status: selectedStatus?.value?.toLowerCase() || undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    enabled: !!ClinicId
  });

  // Invoices list from API (memoized to keep stable reference)
  // When selectedStatus changes, clear immediately before new data arrives
  const apiInvoices = useMemo(() => {
    // If status filter changed and we're waiting for new data, return empty
    // This prevents mixing old data from different status filters
    return data?.objects ?? [];
  }, [data?.objects]);

  // Status chips from meta.status_counts
  const statusSummary = useMemo(() => {
    const counts = data?.meta.status_counts;
    if (!counts) return [];
    return [
      { value: 'Billed', count: counts.billed ?? 0 },
      { value: 'Unbilled', count: counts.unbilled ?? 0 },
      { value: 'Draft', count: counts.draft ?? 0 },
      { value: 'Unsettled', count: counts.unsettled ?? 0 },
      { value: 'Dispensed', count: counts.dispensed ?? 0 },
    ];
  }, [data]);

  // Optional client-side fallback filtering by search (if server ignores search_query)
  // Server-side search is primary; client-side is defensive backup
  const displayedInvoices = useMemo(() => {
    // Don't clear data while fetching - let stale data show to avoid flash of "No invoices"
    // Loading indicator will show user that refresh is happening
    
    if (!search) return apiInvoices;
    const s = search.toLowerCase();

    return apiInvoices.filter(inv => {
      // Check both patient and patient_name fields for name match
      const patientName = (inv.patient ?? inv.patient_name ?? '').toString().toLowerCase();
      const billNumber = (inv.bill_number ?? '').toString().toLowerCase();
      const mrn = (inv.patient_mrn_no ?? '').toString().toLowerCase();

      return patientName.includes(s) || billNumber.includes(s) || mrn.includes(s);
    });
  }, [apiInvoices, search]);

  // Pagination from total_count
  const totalPages = useMemo(() => {
    const total = data?.meta.total_count || 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [data, pageSize]);

  // Replace InvoiceTable data prop
  // NOTE: Adapt InvoiceTable to expect fields from InvoiceItem (patient, visit_date, etc.)
  // If it still expects mock shape, map here.
  // Helper: normalize status from API to table expected casing
  const normalizeStatus = (s: string): 'Billed' | 'Unsettle' | 'Unbilled' | 'Draft' | 'Dispensed' => {
    const v = (s || '').toLowerCase();
    switch (v) {
    // Billed/settled states
    case 'billed':
    case 'paid':
      return 'Billed';
    case 'settled':
      return 'Billed';
    // Needs settlement / partially paid
    case 'unsettled':
    case 'unpaid':
    case 'partially_paid':
    case 'partial':
      return 'Unsettle';
    // Not billed yet
    case 'unbilled':
    case 'pending':
      return 'Unbilled';
    case 'draft':
      return 'Draft';
    // Pharmacy dispensed
    case 'dispensed':
      return 'Dispensed';
    default:
      return 'Unbilled';
    }
  };

  const mapBillTypeLabel = (billType: string | null | undefined): string => {
    if (!billType) return '';
    if (billType === 'PH') return 'Pharmacy';
    return billType;
  };

  const tableData = useMemo(
    () =>
      displayedInvoices.map(inv => ({
        // API-style identifiers (required by InvoiceData)
        patient_id: inv.patient_id,
        appointment_id: inv.appointment_id ?? null,
        id: inv.id ?? null,

        // camelCase aliases for convenience (also in InvoiceData)
        patientId: inv.patient_id,
        mrn: inv.patient_mrn_no ?? undefined,
        appointmentId: inv.appointment_id ?? null,

        date: inv.visit_date || '',
        patientName: `${inv.patient ?? inv?.patient_name ?? ''}${inv.age ? ` | ${inv.age}` : ''}` || '',
        status: normalizeStatus(inv.status || inv.type || ''),
        billNo: inv.bill_number || '',
        clinic: clinicName || '', // assuming clinic source
        billType: mapBillTypeLabel(inv.bill_type),
        provider: inv.Provider || inv.treatment_doctor_name || '',
        invoiceDueStatus: inv.invoice_due_status ?? '',
        totalAmt: inv.total_amount != null ? String(inv.total_amount) : '',
        concessionAmt: inv.concession_amount != null ? String(inv.concession_amount) : '',
        netAmt: inv.net_amount != null ? String(inv.net_amount) : '',
        paidAmt: inv.paid_amount != null ? String(inv.paid_amount) : '',
        balanceAmt: inv.balance_amount != null ? String(inv.balance_amount) : '',
        billRemark: inv.remark ,
        actions: '',
        paymentSource: inv.payment_source ?? null,
        label: inv.label ?? null,
        labelMrnNo: inv.label_mrn_no ?? null,
        can_settle_invoice: inv.can_settle_invoice ?? true,
        invoice_patient_type: inv.invoice_patient_type ?? null,
        is_donor_tagged: inv.is_donor_tagged ?? false,
        advance_consumption_metadata: inv.advance_consumption_metadata ?? null,
        delink_log: inv.delink_log ?? null,
        patientInfo:
          `${inv.patient_mrn_no || ''}${inv.patient_mrn_no && inv.gender ? ' | ' : ''}${inv.gender || ''}`
            .trim() || '',
        clinic_id: inv.clinic_id != null && !isNaN(Number(inv.clinic_id)) ? Number(inv.clinic_id) : null,
        payment_count: inv.payment_count ?? 0,
      })),
    [displayedInvoices, clinicName]
  );

  // Error alert
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Invoice fetch error', error);
    }
  }, [error]);

  // Category counts from API (patient / donor / surrogate / cryo / company)
  const categoryCounts = useMemo(() => {
    const cc = data?.meta.category_count;
    return {
      patient: cc?.patient ?? 0,
      donor: cc?.donor ?? 0,
      surrogate: cc?.surrogate ?? 0,
      cryo: cc?.cryo ?? 0,
      company: cc?.company ?? 0
    };
  }, [data]);

  // Build tab definitions with counts
  const categoryTabs = useMemo(
    () => [
      { label: tBilling('invoice.tabs.patient'), name: 'Patient', count: categoryCounts.patient },
      { label: tBilling('invoice.tabs.donor'), name: 'Donor', count: categoryCounts.donor },
      { label: tBilling('invoice.tabs.company'), name: 'Company', count: categoryCounts.company },
      { label: tBilling('invoice.tabs.surrogate'), name: 'Surrogate', count: categoryCounts.surrogate },
      { label: tBilling('invoice.tabs.cryo_billing'), name: 'Cryo-Billing', count: categoryCounts.cryo }
    ],
    [categoryCounts, tBilling]
  );

  // Derived: whether any filter is active
  const isFiltered = useMemo(
    () => !!(filterValues.fromDate || filterValues.toDate || filterValues.billType),
    [filterValues]
  );

  // Build a compact summary label for the active filters
  const filterSummaryLabel = useMemo(() => {
    const parts: string[] = [];
    if (filterValues.fromDate) parts.push(`From: ${formatDateDMY(filterValues.fromDate)}`);
    if (filterValues.toDate) parts.push(`To: ${formatDateDMY(filterValues.toDate)}`);
    if (filterValues.billType) {
      const typeLabel = filterValues.billType === 'PH' ? 'Pharmacy' : filterValues.billType;
      parts.push(`Type: ${typeLabel}`);
    }
    return parts.join(' • ');
  }, [filterValues]);

  // --- Render ---  
  return (
    <Box className="invoice-page">
      {/* Header: Tabs, Search, New Invoice */}
      <InvoiceHeader
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        categories={categoryTabs}
        search={search}
        onSearchChange={handleSearchChange}
        onOpenFilter={handleOpenLegacyFilter}  // Legacy Modal trigger
        // New: hint dot over filter icon when filters are applied
        showFilterHint={isFiltered}
        onNewInvoice={handleNewInvoice}
      />

      {/* Active filter badge (top-right) */}
      {isFiltered && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 1 }}>
          <Chip
            data-testid="invoice-filter-badge-clear"
            label={filterSummaryLabel || 'Filters active'}
            color="primary"
            variant="outlined"
            onDelete={() => {
              setFilterValues({ fromDate: '', toDate: '', billType: '' });
              setCurrentPage(1);
            }}
            sx={{
              borderRadius: '16px',
              fontSize: '0.8rem',
              maxWidth: '100%',
            }}
          />
        </Box>
      )}


      {/* Legacy Modal */}
      <InvoiceFilter
        open={filterOpenLegacy}
        onClose={handleCloseLegacyFilter}
        onApply={handleApplyFilter}
        billTypeOptions={[
          { value: '', label: tBilling('invoice.filter.select_bill_type') },
          { value: 'Clinical', label: tBilling('invoice.filter.clinical') },
          { value: 'PH', label: tBilling('invoice.filter.pharmacy') },
          { value: 'Package', label: tBilling('invoice.filter.package') },
        ]}
        // Pass current filters so reopening shows previous applied values
        initialValues={filterValues}
      />

      {/* Table with loader */}
      <InvoiceTable data={tableData} loading={isFetching} />

      {/* Pagination & Status Badges */}
      <InvoicePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalEntries={data?.meta.total_count || 0}
        pageSize={pageSize}
        pageSizeOptions={[10, 15, 25, 50]}
        onPageSizeChange={handlePageSizeChange}
        statusBadges={
          <InvoiceStatusChipGroup
            summary={statusSummary}
            selectedStatus={selectedStatus}
            onStatusSelect={handleStatusSelect}
          />
        }
        rightContent={
          <ResetButton onClick={handleReset} sx={{ ml: 1, height: '30px' }} />
        }
      />
    </Box>
  );
};

export default Invoice;
